import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useSession } from 'next-auth/react';
import { ShopTypes } from '~/lib/data-contract';
import { addUser } from '../../operations/mit-fog';

export default function useAddUser() {
    const { data } = useSession();
    const queryClient = useQueryClient();
    const token = data?.accessToken ?? null;

    function mutationFn({ users }: { users: ShopTypes.UserDto }) {
        return addUser({
            users,
            token,
        });
    }

    return useMutation<ShopTypes.UserDto, HTTPError | Error, Arguments, Context>(mutationFn, {
        onMutate: async ({ users }: Arguments) => {
            await queryClient.cancelQueries(['userList', { customerId: users.customerId }]);

            const previousUserList = queryClient.getQueryData<ShopTypes.CustomerUsersResponse>([
                'userList',
                { customerId: users.customerId },
            ]);

            if (previousUserList) {
                queryClient.setQueryData<ShopTypes.CustomerUsersResponse>(
                    ['userList', { customerId: users.customerId }],
                    {
                        ...previousUserList,
                        users: [...previousUserList.users],
                    },
                );
            }

            return { previousUserList };
        },
        onError: (_error, _, context) => {
            const userBeforeMutation = context?.previousUserList;
            if (userBeforeMutation) {
                queryClient.setQueryData(
                    ['userList', { customerId: userBeforeMutation.customerId }],
                    userBeforeMutation,
                );
            }
        },
        onSettled: (_error, _, variables) => {
            queryClient.invalidateQueries(['userList', { customerId: variables.users.customerId }]);
        },
    });
}

type Arguments = { users: ShopTypes.UserDto };
type Context = {
    previousUserList: ShopTypes.CustomerUsersResponse | undefined;
};
